// https://fluentsite.z22.web.core.windows.net/quick-start
import {
  FluentProvider,
  teamsLightTheme,
  teamsDarkTheme,
  teamsHighContrastTheme,
  tokens,
} from "@fluentui/react-components";
import React, { useEffect, useState } from "react";
import { HashRouter as Router, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import Privacy from "./Privacy";
import TermsOfUse from "./TermsOfUse";
import Tab from "./Tab";
import TabConfig from "./TabConfig";
import NotificationDetail from "./NotificationDetail";
import { TeamsFxContext } from "./Context";
import config from "./sample/lib/config";
import { PlatformContext } from "../services/platformContext";
// Only takes effect for proxy purpose when REACT_APP_HOOK_FOR_PROXY is set in the environment.
import "./HookForProxy";

const isWebPlatform = process.env.REACT_APP_PLATFORM === 'web';

// Lazy load Teams SDK to avoid network permission prompts in standalone mode
let teamsApp: any = null;
const getTeamsApp = async () => {
  if (!teamsApp) {
    const teams = await import("@microsoft/teams-js");
    teamsApp = teams.app;
  }
  return teamsApp;
};

/**
 * Teams notification app - for deep links from activity feed (no SSO)
 */
function TeamsNotificationApp() {
  const [deepLinkParams, setDeepLinkParams] = useState<{ cardId: string; boardId: string; teamId?: string; channelId?: string; actionType?: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getTeamsApp().then(async (app) => {
      await app.initialize();
      try {
        const context = await app.getContext();
        const subEntityId = context.page?.subPageId;
        if (subEntityId) {
          const params = JSON.parse(subEntityId);
          if (params.cardId && params.boardId) {
            setDeepLinkParams(params);
          }
        }
      } catch (e) {
        console.error('Failed to get Teams context:', e);
      }
      app.notifySuccess();
      setLoading(false);
    });
  }, []);

  return <AppContent loading={loading} theme={undefined} themeString="default" teamsUserCredential={undefined} deepLinkParams={deepLinkParams} />;
}

/**
 * Teams app wrapper - only used when in Teams context (with SSO)
 * Note: useTeamsUserCredential is dynamically imported to avoid loading Teams SDK in standalone mode
 */
function TeamsApp() {
  const [teamsCredHook, setTeamsCredHook] = useState<any>(null);
  const [hookLoading, setHookLoading] = useState(true);

  useEffect(() => {
    // Dynamically import useTeamsUserCredential to avoid loading Teams SDK in standalone mode
    import("@microsoft/teamsfx-react").then(({ useTeamsUserCredential }) => {
      setTeamsCredHook(() => useTeamsUserCredential);
      setHookLoading(false);
    });
  }, []);

  if (hookLoading) {
    return null;
  }

  return <TeamsAppInner useTeamsUserCredential={teamsCredHook} />;
}

function TeamsAppInner({ useTeamsUserCredential }: { useTeamsUserCredential: any }) {
  const { loading, theme, themeString, teamsUserCredential } = useTeamsUserCredential({
    initiateLoginEndpoint: config.initiateLoginEndpoint!,
    clientId: config.clientId!,
  });
  const [teamsPlatformContext, setTeamsPlatformContext] = useState<PlatformContext | undefined>(undefined);

  useEffect(() => {
    if (loading) {
      getTeamsApp().then(async (app) => {
        await app.initialize();
        app.notifySuccess();
      });
    }
  }, [loading]);

  useEffect(() => {
    if (!loading) {
      import("../services/teamsPlatformContext").then(({ TeamsPlatformContext }) => {
        setTeamsPlatformContext(new TeamsPlatformContext());
      });
    }
  }, [loading]);

  return <AppContent loading={loading || !teamsPlatformContext} theme={theme} themeString={themeString} teamsUserCredential={teamsUserCredential} platformContext={teamsPlatformContext} deepLinkParams={null} />;
}

/**
 * Standalone app wrapper - used when not in Teams (readonly links, privacy/terms pages)
 */
function StandaloneApp() {
  const [standalonePlatform, setStandalonePlatform] = useState<PlatformContext | undefined>(undefined);

  useEffect(() => {
    // Provide a minimal platform context for readonly/standalone mode
    setStandalonePlatform({
      platform: 'web',
      async initialize() {},
      async getUserInfo() { return null; },
      async getAuthToken() { return null; },
      getContext() { return { isInChannel: false, isInChat: false }; },
      getContextPrefix() { return 'standalone'; },
      getBoardId() { return 'standalone-board'; },
      async getAvailableMembers() { return { members: [], error: false }; },
      supportsNotifications() { return false; },
    });
  }, []);

  return <AppContent loading={!standalonePlatform} theme={undefined} themeString="default" teamsUserCredential={undefined} platformContext={standalonePlatform} deepLinkParams={null} />;
}

/**
 * Web app wrapper - full web platform experience (no Teams dependencies)
 */
function WebApp() {
  const [webPlatformContext, setWebPlatformContext] = useState<PlatformContext | undefined>(undefined);
  const [authenticated, setAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [webTheme, setWebTheme] = useState<string>(() => localStorage.getItem('kanban_web_theme') || 'default');
  const [webUser, setWebUser] = useState<{ displayName: string; email: string } | null>(null);

  useEffect(() => {
    const init = async () => {
      const { WebAuthService } = await import("../services/webAuthService");
      const authService = WebAuthService.getInstance();

      // Check if this is a redirect back from Microsoft SSO
      try {
        const { PublicClientApplication, BrowserCacheLocation } = await import("@azure/msal-browser");
        const msalApp = new PublicClientApplication({
          auth: {
            clientId: '1edcf3e5-80b7-4ada-96d7-b6c6ebab0624',
            authority: 'https://login.microsoftonline.com/common',
            redirectUri: window.location.origin,
          },
          cache: { cacheLocation: BrowserCacheLocation.SessionStorage },
        });
        await msalApp.initialize();
        const response = await msalApp.handleRedirectPromise();
        if (response?.account) {
          const account = response.account;
          const msEmail = (account.username || '').toLowerCase();
          const msName = account.name || msEmail.split('@')[0];
          const msId = (account.idTokenClaims as any)?.oid || account.localAccountId || account.homeAccountId;
          console.log('MSAL account IDs:', { oid: (account.idTokenClaims as any)?.oid, localAccountId: account.localAccountId, homeAccountId: account.homeAccountId });
          await authService.microsoftLogin(msEmail, msName, msId);
          const user = authService.getCurrentUser();
          if (user) {
            setWebUser({ displayName: user.displayName, email: user.email });
            setAuthenticated(true);
          }
          setAuthChecked(true);
          return;
        }
      } catch (err) {
        console.error('MSAL redirect handling error:', err);
      }

      // Normal auth check (JWT in localStorage)
      if (authService.isAuthenticated()) {
        authService.validateToken().then((user) => {
          if (user) {
            setAuthenticated(true);
            setWebUser({ displayName: user.displayName, email: user.email });
          }
          setAuthChecked(true);
        });
      } else {
        setAuthChecked(true);
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (authenticated && !webPlatformContext) {
      import("../services/webPlatformContext").then(({ WebPlatformContext }) => {
        setWebPlatformContext(new WebPlatformContext());
      });
    }
  }, [authenticated, webPlatformContext]);

  const handleAuthenticated = () => {
    import("../services/webAuthService").then(({ WebAuthService }) => {
      const user = WebAuthService.getInstance().getCurrentUser();
      if (user) {
        setWebUser({ displayName: user.displayName, email: user.email });
      }
    });
    setAuthenticated(true);
  };

  const handleThemeChange = (theme: string) => {
    setWebTheme(theme);
    localStorage.setItem('kanban_web_theme', theme);
  };

  const handleLogout = () => {
    import("../services/webAuthService").then(({ WebAuthService }) => {
      WebAuthService.getInstance().logout();
      setAuthenticated(false);
      setWebUser(null);
      setWebPlatformContext(undefined);
    });
  };

  if (!authChecked) {
    return null;
  }

  if (!authenticated) {
    const LoginPage = React.lazy(() =>
      import("./LoginPage").then((mod) => ({ default: mod.LoginPage }))
    );
    const ResetPasswordPage = React.lazy(() =>
      import("./ResetPasswordPage").then((mod) => ({ default: mod.ResetPasswordPage }))
    );
    // Check if we're on the reset-password route
    const isResetRoute = window.location.hash.includes('reset-password');
    return (
      <FluentProvider
        theme={{ ...teamsLightTheme, colorNeutralBackground3: "#eeeeee" }}
        style={{ background: tokens.colorNeutralBackground3 }}
      >
        <React.Suspense fallback={null}>
          {isResetRoute ? (
            <Router>
              <Routes>
                <Route path="/reset-password" element={<ResetPasswordPage onAuthenticated={handleAuthenticated} />} />
                <Route path="*" element={<LoginPage onAuthenticated={handleAuthenticated} />} />
              </Routes>
            </Router>
          ) : (
            <LoginPage onAuthenticated={handleAuthenticated} />
          )}
        </React.Suspense>
      </FluentProvider>
    );
  }

  const WebHeader = React.lazy(() =>
    import("./WebHeader").then((mod) => ({ default: mod.WebHeader }))
  );

  const theme = webTheme === "dark" ? teamsDarkTheme : { ...teamsLightTheme, colorNeutralBackground3: "#eeeeee" };

  return (
    <TeamsFxContext.Provider value={{ theme, themeString: webTheme, teamsUserCredential: undefined, platformContext: webPlatformContext }}>
      <FluentProvider
        theme={theme}
        style={{ background: tokens.colorNeutralBackground3, height: '100vh', display: 'flex', flexDirection: 'column' }}
      >
        <React.Suspense fallback={null}>
          <WebHeader
            userName={webUser?.displayName || 'User'}
            userEmail={webUser?.email || ''}
            themeString={webTheme}
            onThemeChange={handleThemeChange}
            onLogout={handleLogout}
          />
        </React.Suspense>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <Router>
            {!webPlatformContext ? null : (
              <Routes>
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/termsofuse" element={<TermsOfUse />} />
                <Route path="/tab" element={<Tab />} />
                <Route path="*" element={<Navigate to={"/tab"} />} />
              </Routes>
            )}
          </Router>
        </div>
      </FluentProvider>
    </TeamsFxContext.Provider>
  );
}

/**
 * Component to handle deep link navigation
 */
function DeepLinkHandler({ deepLinkParams }: { deepLinkParams: { cardId: string; boardId: string; teamId?: string; channelId?: string; actionType?: string } | null }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (deepLinkParams) {
      const { cardId, boardId, teamId, channelId, actionType } = deepLinkParams;
      const url = `/notification-detail?cardId=${encodeURIComponent(cardId)}&boardId=${encodeURIComponent(boardId)}&teamId=${teamId || ''}&channelId=${channelId || ''}&actionType=${actionType || ''}`;
      console.log('Navigating to deep link URL:', url);
      navigate(url);
    }
  }, [deepLinkParams, navigate]);

  return null;
}

/**
 * Shared app content
 */
function AppContent({ loading, theme, themeString, teamsUserCredential, platformContext, deepLinkParams }: {
  loading: boolean;
  theme: any;
  themeString: string;
  teamsUserCredential: any;
  platformContext?: PlatformContext;
  deepLinkParams: { cardId: string; boardId: string; teamId?: string; channelId?: string } | null;
}) {
  return (
    <TeamsFxContext.Provider value={{ theme, themeString, teamsUserCredential, platformContext }}>
      <FluentProvider
        theme={
          themeString === "dark"
            ? teamsDarkTheme
            : themeString === "contrast"
            ? teamsHighContrastTheme
            : {
                ...teamsLightTheme,
                colorNeutralBackground3: "#eeeeee",
              }
        }
        style={{ background: tokens.colorNeutralBackground3 }}
      >
        <Router>
          <DeepLinkHandler deepLinkParams={deepLinkParams} />
          {!loading && (
            <Routes>
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/termsofuse" element={<TermsOfUse />} />
              <Route path="/tab" element={<Tab />} />
              <Route path="/config" element={<TabConfig />} />
              <Route path="/notification-detail" element={<NotificationDetail />} />
              <Route path="*" element={<Navigate to={"/tab"} />}></Route>
            </Routes>
          )}
        </Router>
      </FluentProvider>
    </TeamsFxContext.Provider>
  );
}

/**
 * Check if running inside an iframe (safely handles cross-origin restrictions)
 */
function isInIframe(): boolean {
  try {
    return typeof window !== 'undefined' && window.self !== window.top;
  } catch (e) {
    // Cross-origin iframe - we're definitely in an iframe (likely Teams)
    return true;
  }
}

function isLikelyTeams(): boolean {
  // Check iframe first (desktop Teams)
  if (isInIframe()) return true;
  // Teams mobile adds these URL params when opening tabs
  const params = new URLSearchParams(window.location.search);
  if (params.has('hostName') || params.has('locale') || params.has('theme')) return true;
  // Teams mobile user agent
  if (typeof navigator !== 'undefined' && /Teams/i.test(navigator.userAgent)) return true;
  return false;
}

/**
 * Wrapper that checks Teams context first before deciding on SSO
 */
function TeamsContextChecker() {
  const [mode, setMode] = useState<'loading' | 'notification' | 'full'>('loading');
  const [deepLinkParams, setDeepLinkParams] = useState<{ cardId: string; boardId: string; actionType?: string } | null>(null);
  const [themeString, setThemeString] = useState<string>('default');

  useEffect(() => {
    getTeamsApp().then(async (app) => {
      await app.initialize();
      try {
        const context = await app.getContext();

        // Get theme from Teams context
        const teamsTheme = context.app?.theme || 'default';
        setThemeString(teamsTheme);

        // Register theme change handler
        app.registerOnThemeChangeHandler((theme: string) => {
          setThemeString(theme);
        });

        const subEntityId = context.page?.subPageId;

        if (subEntityId) {
          // subEntityId can be JSON (from notification deep links) or a plain card ID (from navigateToApp)
          try {
            const params = JSON.parse(subEntityId);
            if (params.cardId && params.boardId) {
              // Notification deep link — show notification detail page (no SSO needed)
              setDeepLinkParams(params);
              setMode('notification');
              app.notifySuccess();
              return;
            }
          } catch (e) {
            // Plain card ID from navigateToApp — fall through to full app with SSO
          }
        }

        // Not a notification - use full app with SSO
        setMode('full');
      } catch (e) {
        console.error('Failed to get Teams context:', e);
        setMode('full');
      }
    });
  }, []);

  if (mode === 'loading') {
    return null; // Or a loading spinner
  }

  if (mode === 'notification') {
    return <AppContent loading={false} theme={undefined} themeString={themeString} teamsUserCredential={undefined} deepLinkParams={deepLinkParams} />;
  }

  // Full mode - render TeamsApp with SSO
  return <TeamsApp />;
}

/**
 * The main app which handles the initialization and routing
 * of the app.
 */
export default function App() {
  // Web platform — no Teams dependencies, no redirects
  if (isWebPlatform) {
    return <WebApp />;
  }

  // --- Teams platform (default) ---

  // Check hash for notification-detail route (direct navigation)
  if (window.location.hash.includes('notification-detail')) {
    return <StandaloneApp />;
  }

  // Allow shared read-only board links in a browser
  const params = new URLSearchParams(window.location.search);
  if (params.get('readonly') === 'true' && params.get('boardId')) {
    return <StandaloneApp />;
  }

  // Allow privacy and terms pages to load in a browser
  if (window.location.hash.includes('privacy') || window.location.hash.includes('termsofuse')) {
    return <StandaloneApp />;
  }

  // If not in Teams, redirect to marketing homepage
  if (!isLikelyTeams()) {
    window.location.href = process.env.REACT_APP_MARKETING_URL || 'https://kanbanprojecttracker.com';
    return null;
  }

  // Render Teams app
  return <TeamsContextChecker />;
}
